import { useMemo } from 'react';
import { useMainLayout } from '../../layouts/MainPageLayout/MainLayoutContext';
import { Box, Select, Text } from '@zillow/constellation-10';
import { US_REGION_ID } from '../../../constants';

import { DisplayingRegionLevel } from '../../../types/DisplayingRegionLevel';
import { styled } from '@/styled-system/jsx';

const MarketDropdownWrapper = styled(Box, {
  base: {
    display: 'flex',
    alignItems: 'center',
  },
  variants: {
    hideLine: {
      true: {},
      false: {
        _after: {
          content: '""',
          width: '1px',
          height: '24px',
          backgroundColor: 'border.muted',
          marginInlineStart: '200',
        },
      },
    },
  },
});

export const MarketDropdown = ({
  labelText = 'Comparison market',
  hideMarketSelect = false,
  hideLine = false,
}: {
  labelText?: string;
  hideMarketSelect?: boolean;
  hideLine?: boolean;
}) => {
  const {
    allProperties,
    regionsMapping,
    selectedMarketId,
    selectedRegionId,
    selectedDisplayingRegionLevel,
    setSelectedMarketId,
    selectedFilters,
  } = useMainLayout();

  let selectedProperty = allProperties.find(
    (property) => property.propertyDetails.listingAlias === selectedFilters.listingAlias,
  );

  if (!selectedProperty && selectedRegionId) {
    selectedProperty = allProperties.find(
      (property) =>
        Number(property.propertyDetails.stateRegionId) === Number(selectedRegionId) ||
        Number(property.propertyDetails.metroStatRegionId) === Number(selectedRegionId) ||
        Number(property.propertyDetails.cityRegionId) === Number(selectedRegionId) ||
        Number(property.propertyDetails.zipRegionId) === Number(selectedRegionId),
    );
  }

  const allStates = useMemo(
    () => [...new Set(allProperties.map((p) => p.propertyDetails.stateRegionId))],
    [allProperties],
  );

  const marketOptions = useMemo(() => {
    if (selectedProperty) {
      const options = [];

      if (regionsMapping[selectedProperty.propertyDetails.stateRegionId]) {
        options.push({
          value: selectedProperty.propertyDetails.stateRegionId,
          label: regionsMapping[selectedProperty.propertyDetails.stateRegionId],
        });
      }

      if (
        regionsMapping[selectedProperty.propertyDetails.metroStatRegionId] &&
        selectedDisplayingRegionLevel !== DisplayingRegionLevel.STATE
      ) {
        options.push({
          value: selectedProperty.propertyDetails.metroStatRegionId,
          label: regionsMapping[selectedProperty.propertyDetails.metroStatRegionId],
        });
      }

      if (
        regionsMapping[selectedProperty.propertyDetails.cityRegionId] &&
        ![DisplayingRegionLevel.STATE, DisplayingRegionLevel.METRO_AREA].includes(selectedDisplayingRegionLevel)
      ) {
        options.push({
          value: selectedProperty.propertyDetails.cityRegionId,
          label: regionsMapping[selectedProperty.propertyDetails.cityRegionId],
        });
      }

      if (selectedFilters.listingAlias !== '')
        setSelectedMarketId(
          Number(
            selectedProperty.propertyDetails.cityRegionId ||
              selectedProperty.propertyDetails.metroStatRegionId ||
              selectedProperty.propertyDetails.stateRegionId,
          ),
        );
      else setSelectedMarketId(selectedRegionId);

      return options;
    }

    if (allStates.length === 1) {
      setSelectedMarketId(allStates[0]);
    } else {
      setSelectedMarketId(US_REGION_ID);
    }

    return allStates
      .filter((stateRegionId) => Boolean(stateRegionId))
      .map((stateRegionId) => ({
        value: stateRegionId,
        label: regionsMapping[stateRegionId],
      }))
      .filter((state) => Boolean(state.label))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [
    selectedProperty,
    allStates,
    regionsMapping,
    selectedFilters.listingAlias,
    setSelectedMarketId,
    selectedRegionId,
    selectedDisplayingRegionLevel,
  ]);

  return (
    <>
      {!hideMarketSelect && (
        <MarketDropdownWrapper hideLine={hideLine}>
          <Text textStyle="body-bold" css={{ display: 'inline-block', whiteSpace: 'nowrap', paddingInlineEnd: '200' }}>
            {labelText}
          </Text>
          <Select value={selectedMarketId} onChange={(event) => setSelectedMarketId(Number(event.target.value))}>
            <option value={US_REGION_ID}>Nationwide</option>
            {marketOptions.map(({ value: regionId, label }) => (
              <option key={regionId} value={regionId}>
                {label}
              </option>
            ))}
          </Select>
        </MarketDropdownWrapper>
      )}
    </>
  );
};
